/**
 * The Testing Sandbox's vocabulary (AICC2-334, backend AICC2-528).
 *
 * 🔴 NO LIVEKIT AND NO STORED CONVERSATION. Two plain HTTP endpoints; the backend keeps nothing, spends no
 * quota and synthesises no audio, so the transcript lives in React state and travels on every request.
 */

/**
 * 🔴 THE SAME VALUES AS `AICC_PROMPT_GROUP`, AND STILL ITS OWN CONSTANT: the group keys our URL and tabs, the
 * mode is a request field. Sharing one would hide the day the backend renames a mode.
 */
export const AICC_SANDBOX_MODE = {
  onboarding: "onboarding",
  scenarioConversation: "scenario_conversation",
  lessonReview: "lesson_review",
} as const;

export type AiccSandboxMode = typeof AICC_SANDBOX_MODE[keyof typeof AICC_SANDBOX_MODE];

export const AICC_SANDBOX_MODE_LABEL: Record<AiccSandboxMode, string> = {
  [AICC_SANDBOX_MODE.onboarding]: "Onboarding",
  [AICC_SANDBOX_MODE.scenarioConversation]: "Scenario Conversation",
  [AICC_SANDBOX_MODE.lessonReview]: "Lesson Review",
};

/** `error_correction` runs the grammar half only — pronunciation needs audio, Azure and S3. */
export const AICC_SANDBOX_ACTION = {
  hint: "hint",
  errorCorrection: "error_correction",
  feedback: "feedback",
  foreignWordIdentification: "foreign_word_identification",
} as const;

export type AiccSandboxAction = typeof AICC_SANDBOX_ACTION[keyof typeof AICC_SANDBOX_ACTION];

// 🔴 The sandbox runs stored text only: `working` is the draft, `live` what production runs.
export const AICC_SANDBOX_BASE_SCOPE = {
  working: "working",
  live: "live",
} as const;

export type AiccSandboxBaseScope = typeof AICC_SANDBOX_BASE_SCOPE[keyof typeof AICC_SANDBOX_BASE_SCOPE];

export const AICC_SANDBOX_PROMPT_SOURCE = {
  working: "working",
  version: "version",
  /** The base set, for a language with no configuration of its own. */
  system: "system",
} as const;

export type AiccSandboxPromptSource = typeof AICC_SANDBOX_PROMPT_SOURCE[keyof typeof AICC_SANDBOX_PROMPT_SOURCE];

/**
 * Which instruction the backend appended to this turn's user content.
 *
 * 🔴 THE FRONTEND CANNOT DERIVE IT. Comparing `sent_user_content` with the typed line only says "something was
 * appended", and under the default configuration every armed round is also a reminder round
 * (`SCENARIO_MAX_ROUNDS` is a multiple of `SCENARIO_REANCHOR_EVERY`) — so a round that merely re-anchored would be
 * read as a wrap-up, greying the button a round early and taking away the only way to see the prompt being edited.
 */
export const AICC_SANDBOX_DIRECTIVE = {
  wrapup: "wrapup",
  reminder: "reminder",
} as const;

export type AiccSandboxDirective = typeof AICC_SANDBOX_DIRECTIVE[keyof typeof AICC_SANDBOX_DIRECTIVE];

export const AICC_SANDBOX_END_REASON = {
  llm: "llm",
  graceExhausted: "grace_exhausted",
  /** Lesson review's own ending: the script ran out of learner lines, which no model decided. */
  scriptComplete: "script_complete",
} as const;

export type AiccSandboxEndReason = typeof AICC_SANDBOX_END_REASON[keyof typeof AICC_SANDBOX_END_REASON];

/**
 * How a lesson-review answer was judged (§4.7.3).
 *
 * 🔴 FOUR BRANCHES, AND `retry` IS THE FIRST WRONG ANSWER — not the second. `max_retries` defaults to 2,
 * `retry_count` starts at 0 and the comparison is strictly less than, so wrong answers go hint, hint, reveal.
 *
 * 🔴 `correct` CARRIES NO PRAISE. Production moves straight to the next cue with no prefix, so nothing on this
 * screen may add one — an operator tuning against a "Correct!" the app never says is tuning against us.
 */
export const AICC_SANDBOX_GRADING_OUTCOME = {
  correct: "correct",
  retry: "retry",
  givingUp: "giving_up",
  maxRetries: "max_retries",
} as const;

export type AiccSandboxGradingOutcome = typeof AICC_SANDBOX_GRADING_OUTCOME[keyof typeof AICC_SANDBOX_GRADING_OUTCOME];

/**
 * Which hint prompt to run (§4.3.1).
 *
 * 🔴 NAMED RATHER THAN LEFT TO BE INFERRED. The two hint prompts are variants of ONE flow, chosen by a predicate on
 * the conversation type, and a predicate that matches nothing falls back to the scenario one — so a lesson-review
 * hint asked for without saying so comes back looking perfectly normal, out of the prompt that is not under test.
 */
export const AICC_SANDBOX_HINT_VARIANT = {
  lessonReview: "lesson_review_hint",
};

/**
 * Which feedback prompt to run. BOTH are named; neither is a default.
 *
 * The same trap the hint variant above exists for, one prompt along — and it is worth stating twice because the
 * shape differs: these two are not variants of one flow but two SEPARATE flows, so they carry their own model and
 * temperature as well as their own wording. Picking one from silence is a wrong answer no screen can show, so the
 * backend refuses a feedback call that names neither rather than defaulting — which is why the scenario entry here
 * is load-bearing and not documentation.
 */
export const AICC_SANDBOX_FEEDBACK_VARIANT = {
  scenario: "feedback",
  lessonReview: "feedback_lesson_review",
};

/**
 * 🔴 EXACTLY `"learner"` IS THE LINE BEING GRADED. Anything else is the other role or a narrator action — a stage
 * direction the generator produced ("she hands you a map") — and only the learner's line is what an answer is scored
 * against, which is why the preview marks it.
 */
export const AICC_SANDBOX_LESSON = {
  learnerSpeaker: "learner",
  defaultLevel: 1,
  defaultLesson: 1,
  defaultScriptIndex: 1,
};

/**
 * 🔴 THE SEED TURN'S LITERAL, AND IT CANNOT BE ANYTHING ELSE. The hint service filters the synthetic opening
 * turn by `role === "user" && content === "start"`, which is what production seeds; any other word makes a hint
 * treat it as something the learner said. It is never rendered either.
 */
export const AICC_SANDBOX_SEED_MESSAGE = "start";

/** Production's round limit, and the grace it allows past it. Named out here because the transcript cap
 * is derived from both — an object literal cannot read its own fields. */
const DEFAULT_ROUNDS = 18;

const WRAPUP_GRACE = 8;

/** Onboarding's own limit, which is the LONGER window — and it counts ROUNDS despite the backend
 * naming it `ONBOARDING_MESSAGE_LIMIT`: the processor assigns it to `_max_rounds` and appends a
 * wrap-up on the round it reaches. Onboarding has no Wrap Up Round control, so nothing on screen
 * reveals this number; the transcript cap is the only place it has to be accounted for. */
const ONBOARDING_ROUNDS = 30;

/** A round is a learner turn and the reply to it. */
const TURNS_PER_ROUND = 2;

/** The two entries a seeded opener adds — `[user:"start", assistant:opener]` — which no round accounts
 * for: the backend's `round_count` skips the synthetic `start`, the transcript still carries it. */
const SEEDED_OPENER_TURNS = 2;

/** What the backend refuses rather than truncates. `defaultRounds` is production's; a smaller one is the knob. */
export const AICC_SANDBOX_LIMITS = {
  /**
   * SIZED BY THE LONGEST CONVERSATION THIS PANEL SIMULATES, not by a round figure. ONE CAP SERVES EVERY
   * MODE, so it has to clear the longest of them: a scenario runs to the forced end `wrapupGrace` rounds
   * past its wrap-up, onboarding runs further to its own round limit, and a cap below either refuses the
   * turn that would have closed the conversation out. Lesson review needs neither — its length is the
   * script's, and a long one sits far inside this.
   *
   * THE BACKEND'S `MAX_TRANSCRIPT_TURNS` IS THE AUTHORITY, and it takes the same maximum from the live
   * production settings. This is a client-side EARLY WARNING, so it only has to be no larger: the
   * panel stopping a turn the API would still accept costs an operator nothing, while the reverse is
   * the 400 this warning exists to avoid. The round figures here are copies of production's, so a
   * change there has to reach this file too.
   *
   * A `maxRounds` ABOVE THE DEFAULT STILL OUTGROWS IT, and deliberately: the field accepts far more
   * rounds than this holds. It covers the DEFAULT windows, the ones a prompt is tuned against.
   */
  transcriptTurns: SEEDED_OPENER_TURNS + Math.max(DEFAULT_ROUNDS + WRAPUP_GRACE, ONBOARDING_ROUNDS) * TURNS_PER_ROUND,
  /** Left alone when the turn count grew: a full transcript of ordinary conversation sits well inside
   * it, and raising it would loosen the one cap that bounds a runaway paste. */
  transcriptChars: 40000,
  minRounds: 1,
  maxRounds: 50,
  defaultRounds: DEFAULT_ROUNDS,
  /**
   * 🔴 THE SAME NUMBER FEEDS BOTH RULES: wrap-up is injected at `round_count >= max_rounds` and the conversation
   * is killed at `max_rounds + 8`. That is why lowering the round limit is not a substitute for the Wrap Up
   * button — an operator ten rounds in who sets 2 gets `forced_end` on the spot and a locked input.
   */
  wrapupGrace: WRAPUP_GRACE,
  turnsPerRound: TURNS_PER_ROUND,
  seededOpenerTurns: SEEDED_OPENER_TURNS,
};

/** 1–10, and ≤ 2 is what folds `beginner_rules` into the main prompt. */
export const AICC_SANDBOX_FLUENCY = {
  min: 1,
  max: 10,
  default: 1,
};
